import { Shield, Lock, Server, Users, Mail, CheckCircle, Globe, FileText } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { Link } from 'react-router-dom';

export default function TrustPage() {
  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <SEOHead
        title="Trust & Security | Nuvanta TradePath"
        description="How Nuvanta TradePath protects your data. Data residency, encryption, access controls, and compliance for enterprise and government supply chain teams."
      />

      {/* Header */}
      <header>
        <nav className="bg-[#0A2533] border-b border-white/10" aria-label="Trust page navigation">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-white font-bold text-lg sm:text-xl">Nuvanta Solutions</span>
              <span className="text-[#4DD0E1] text-lg sm:text-xl">|</span>
              <span className="text-[#4DD0E1] font-semibold text-lg sm:text-xl">TradePath</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/signin" className="text-white/80 hover:text-white text-sm transition-colors">Sign in</Link>
              <Link to="/register" className="bg-[#00707C] hover:bg-[#005F6A] text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors">Start your trial</Link>
            </div>
          </div>
        </nav>
      </header>

      <main id="main-content" className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0097A7]/10 mb-6">
            <Shield className="h-8 w-8 text-[#0097A7]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0D3B4F] tracking-tight mb-4" data-testid="trust-heading">
            Trust and security
          </h1>
          <p className="text-[#4E6974] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Enterprise and government supply chain teams trust TradePath with sensitive supplier data. Here is how we protect it.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-12">

          {/* Data residency */}
          <section className="bg-white rounded-xl p-8 shadow-sm" data-testid="trust-data-residency">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#0097A7]/10 flex items-center justify-center flex-shrink-0">
                <Server className="h-5 w-5 text-[#0097A7]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">Data residency</h2>
                <p className="text-[#4E6974] leading-relaxed">
                  All customer data is stored and processed in Australia (AWS ap-southeast-2, Sydney region). Data does not leave the Australian jurisdiction unless explicitly requested by the customer for integration purposes. Our infrastructure is hosted on AWS with Australian data sovereignty guarantees.
                </p>
              </div>
            </div>
          </section>

          {/* Access controls */}
          <section className="bg-white rounded-xl p-8 shadow-sm" data-testid="trust-access-controls">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#0097A7]/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-[#0097A7]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">Access controls</h2>
                <ul className="space-y-2 text-[#4E6974]">
                  {[
                    'Role-based access control (RBAC) with organisation-level data isolation',
                    'Multi-factor authentication (MFA) support for all user accounts',
                    'SSO integration available for enterprise customers',
                    'Least-privilege access model for all internal operations',
                    'Session management with automatic timeout and secure cookie handling',
                    'Audit logging of all user actions and data access events',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-[#2E7D32] mt-0.5 flex-shrink-0" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Certifications */}
          <section className="bg-white rounded-xl p-8 shadow-sm" data-testid="trust-certifications">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#0097A7]/10 flex items-center justify-center flex-shrink-0">
                <FileText className="h-5 w-5 text-[#0097A7]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">Certifications and compliance</h2>
                <ul className="space-y-2 text-[#4E6974]">
                  {[
                    'ISO 27001 certification in progress (expected completion Q4 2026)',
                    'SOC 2 Type II assessment planned for 2027',
                    'DFAT sanctions compliance screening embedded in platform workflows',
                    'Modern Slavery Act reporting capabilities built into supplier analytics',
                    'Privacy Act 1988 (Cth) and Australian Privacy Principles compliant',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-[#2E7D32] mt-0.5 flex-shrink-0" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Data handling */}
          <section className="bg-white rounded-xl p-8 shadow-sm" data-testid="trust-data-handling">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#0097A7]/10 flex items-center justify-center flex-shrink-0">
                <Lock className="h-5 w-5 text-[#0097A7]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">Data handling</h2>
                <ul className="space-y-2 text-[#4E6974]">
                  {[
                    'Encryption at rest: AES-256 for all stored data including databases and backups',
                    'Encryption in transit: TLS 1.3 for all API and web traffic',
                    'Data retention: Customer data retained for the duration of the subscription plus 90 days',
                    'Deletion on request: Full data deletion available within 30 days of written request',
                    'Automated daily backups with point-in-time recovery capability',
                    'No customer data is used for model training or shared with third parties',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-[#2E7D32] mt-0.5 flex-shrink-0" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-[#0D3B4F] rounded-xl p-8 text-center" data-testid="trust-contact">
            <Mail className="h-8 w-8 text-[#4DD0E1] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Security enquiries</h2>
            <p className="text-white/70 mb-4 leading-relaxed">
              For security questions, vulnerability reports, or to request our security documentation, contact our security team directly.
            </p>
            <a
              href="mailto:security@nuvantatradepath.com.au"
              className="inline-flex items-center gap-2 text-[#4DD0E1] font-semibold hover:underline text-lg"
              data-testid="trust-security-email"
            >
              security@nuvantatradepath.com.au
            </a>
          </section>
        </div>

        {/* Back link */}
        <div className="text-center mt-12">
          <Link to="/" className="text-[#0097A7] hover:underline text-sm font-medium">Back to home</Link>
          <span className="text-[#4E6974] mx-3">|</span>
          <Link to="/pricing" className="text-[#0097A7] hover:underline text-sm font-medium">View pricing</Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 bg-[#0A2533]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#4DD0E1] text-sm font-semibold mb-2">
            A <a href="https://nuvantasolutions.com.au" className="underline hover:text-white transition-colors">Nuvanta Solutions</a> product
          </p>
          <p className="text-white/40 text-sm">&copy; 2026 Nuvanta Solutions Pty Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
